import { defineStore } from 'pinia';
import { WebshopCartService } from '@/services/api';
import { Order, OrderItem, Product } from '@/models';
import { useSessionStore } from '@/stores/session.store';

interface State {
    _cart: Order | null;
    isCheckingOut?: boolean;
}

const initialState: State = {
  _cart: null
};

export const useWebshopCartStore = defineStore('webshopCart', {
  state: (): State => ({
    ...initialState
  }),
  getters: {
    cart: (state: State): Order | null => state._cart,
    cartItems: (state: State): OrderItem[] => state._cart?.orderItems ?? [],
    cartItemsCount: (state: State): number => state._cart?.orderItems?.reduce((total, item) => total + item.quantity, 0) || 0
  },
  actions: {
    enrichOrderItemsWithPrivateLabelImage(orderItems?: OrderItem[]): OrderItem[] {
      if (!orderItems?.length) {
        return orderItems || [];
      }
      return orderItems.map((orderItem: OrderItem) => {
        const product: Product | undefined = orderItem.product;
        const image = product?.has_private_label ? product?.image : null;
        if (!image) {
          return orderItem;
        }
        const productOptions = { ...(orderItem.productOptions || {}), image };
        return { ...orderItem, productOptions } as OrderItem;
      });
    },
    getCartService() {
      const sessionStore = useSessionStore();
      return new WebshopCartService(sessionStore.currentOrganisationId);
    },
    async fetchCart() {
      try {
        const cartService = this.getCartService();
        const cart = await cartService.getCart();
        if (cart) {
          cart.orderItems = this.enrichOrderItemsWithPrivateLabelImage(cart.orderItems);
        }
        this._cart = cart;
      } catch (error: any) {
        if (error?.response?.status === 204 || error?.response?.status === 404) {
          this._cart = null;
        } else {
          console.error(`Failed to fetch cart: ${error}`);
          this._cart = null;
          throw new Error(`Failed to fetch cart: ${error}`);
        }
      }
    },
    async addItem(orderItem: OrderItem) {
      try {
        const cartService = this.getCartService();
        const response = await cartService.addItem(orderItem);
        if (response) {
          response.orderItems = this.enrichOrderItemsWithPrivateLabelImage(response.orderItems);
        }
        this._cart = response;
      } catch (error) {
        throw new Error(`Failed to add item to cart: ${error}`);
      }
    },
    async updateItem(itemId: string, orderItem: OrderItem) {
      try {
        const cartService = this.getCartService();
        const updatedItemFromApi = await cartService.updateItem(itemId, orderItem);

        if (!this._cart) {
          this._cart = {
            id: '',
            status: 'pending',
            orderNumber: '',
            orderDate: null,
            comments: null,
            fileBasePath: null,
            parameters: '{}',
            orderItems: []
          };
        }

        if (!this._cart.orderItems) {
          this._cart.orderItems = [];
        }

        const finalItemId = updatedItemFromApi?.id || itemId;
        const existingItemIndex = this._cart.orderItems.findIndex((item) => item.id === finalItemId);

        const orderItems = [...this._cart.orderItems];

        if (existingItemIndex >= 0) {
          orderItems[existingItemIndex] = updatedItemFromApi || orderItem;
        } else if (updatedItemFromApi) {
          orderItems.push(updatedItemFromApi);
        }

        this._cart.orderItems = this.enrichOrderItemsWithPrivateLabelImage(orderItems);

        return this._cart.orderItems.find((item) => item.id === finalItemId) || null;
      } catch (error) {
        console.error('Failed to update item:', error);
        throw error;
      }
    },
    async removeItem(itemId: string) {
      try {
        const cartService = this.getCartService();
        await cartService.removeItem(itemId);
        if (this._cart?.orderItems) {
          this._cart.orderItems = this._cart.orderItems.filter((item) => item.id !== itemId);
        }
      } catch (error) {
        throw new Error(`Failed to remove item from cart: ${error}`);
      }
    },
    async checkout() {
      try {
        const cartService = this.getCartService();
        await cartService.checkout();
        this._cart = null;
      } catch (error) {
        throw new Error(`Failed to checkout: ${error}`);
      }
    }
  }
});
